import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useUIContext } from "../../../../context/UIContext";
import useSurveyQuestions from "../../../../hooks/useSurveyQuestions";
import TeacherQuestionItem from "./TeacherQuestionItem";
import styles from "./ModifyingSurvey.module.css";

export default function ModifyingSurvey({
  lessonInfo,
  onClose,
  qualifyingSurveys = [],
  createOrUpdateSurveySubjectLink,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];
  const { handleToastCenterTop, setToastConfirmModalState } = useUIContext();

  // 🔥 질문 타입을 i18next 키로 변환하는 함수
  const getQuestionTypeLabel = (questionType) => {
    const typeMap = {
      short_answer: "shortAnswer",
      long_answer: "longAnswer",
      multiple_choice: "multipleChoice",
      checkbox: "checkbox",
    };
    return t(`common.survey.${typeMap[questionType] || questionType}`);
  };

  const [selectedSurveyId, setSelectedSurveyId] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [editingTeacherQuestions, setEditingTeacherQuestions] = useState({});
  const [teacherQuestionOrder, setTeacherQuestionOrder] = useState([]);
  const [draggedQuestion, setDraggedQuestion] = useState(null);
  const [dragOverTargetId, setDragOverTargetId] = useState(null);
  // 🔥 기존 state들 아래에 추가
  const [isPublished, setIsPublished] = useState(false);
  const [isUpdatingPublishStatus, setIsUpdatingPublishStatus] = useState(false);

  // 선택된 설문 정보
  const selectedSurvey = useMemo(() => {
    return qualifyingSurveys.find(
      (survey) => survey.id === parseInt(selectedSurveyId)
    );
  }, [selectedSurveyId, qualifyingSurveys]);

  // 설문 질문 조회 (선택된 설문이 있을 때만)
  const {
    surveyQuestionsAllQuery,
    createOrUpdateSurveyQuestions,
    deleteSurveyQuestion,
  } = useSurveyQuestions({
    schoolid: schoolId,
    survey_id: selectedSurvey?.id,
  });

  // 🔥 기본 질문 데이터 (서버에서 가져온 원본)
  const { commonQuestions, teacherQuestions } = useMemo(() => {
    const allQuestions = surveyQuestionsAllQuery?.data || [];

    // 공통 질문 (is_common=true)
    const common = allQuestions.filter((q) => q.is_common === true);

    // 교사 질문 (is_common=false이고 현재 과목의 subject_id와 일치)
    const teacher = allQuestions.filter(
      (q) => q.is_common === false && q.subject_id === lessonInfo?.id
    );

    return {
      commonQuestions: common.sort((a, b) => a.order - b.order),
      teacherQuestions: teacher,
    };
  }, [surveyQuestionsAllQuery?.data, lessonInfo?.id]);

  // 🔥 표시용 교사 질문 (순서와 편집 상태 반영)
  const displayTeacherQuestions = useMemo(() => {
    // 순서 상태가 있으면 그것을, 없으면 서버 데이터를 정렬
    let baseQuestions;
    if (teacherQuestionOrder.length > 0) {
      baseQuestions = teacherQuestionOrder;
    } else {
      baseQuestions = [...teacherQuestions].sort((a, b) => a.order - b.order);
    }

    // editingTeacherQuestions와 합쳐서 실제 표시용 데이터 반환
    return baseQuestions.map((question) => ({
      ...question,
      ...editingTeacherQuestions[question.id],
    }));
  }, [teacherQuestions, teacherQuestionOrder, editingTeacherQuestions]);

  // 🔥 교사 질문 수정 시 임시 상태 업데이트
  const handleTeacherQuestionChange = (id, field, value) => {
    // 질문 타입이 객관식/체크박스로 변경되고 옵션이 없는 경우 기본 옵션 추가
    let finalValue = value;
    if (field === "question_type") {
      if (value === "multiple_choice" || value === "checkbox") {
        // 객관식/체크박스로 변경 시 옵션이 없으면 기본 옵션 추가
        const currentQuestion = displayTeacherQuestions.find(
          (q) => q.id === id
        );
        if (!currentQuestion?.options || currentQuestion.options.length === 0) {
          handleTeacherQuestionChange(id, "options", [
            {
              id: -Date.now(), // 임시 ID 추가
              schoolid: schoolId, // schoolid 추가
              option_text: `${
                t("common.survey.optionPlaceholder") || "옵션"
              } 1`,
              order: 1,
            },
          ]);
        }
      } else if (value === "short_answer" || value === "long_answer") {
        // 단답형/장문형으로 변경 시 옵션 초기화
        handleTeacherQuestionChange(id, "options", []);
      }
    }

    setEditingTeacherQuestions((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: finalValue,
      },
    }));

    // teacherQuestionOrder 상태도 업데이트 (화면 즉시 반영)
    setTeacherQuestionOrder((prevOrder) =>
      prevOrder.map((q) => (q.id === id ? { ...q, [field]: finalValue } : q))
    );

    // 🔥 질문 수정 시 변경사항 있음으로 표시
    setHasUnsavedChanges(true);
  };

  // 🔥 옵션 관련 함수들
  const addTeacherQuestionOption = (questionId) => {
    const question = displayTeacherQuestions.find((q) => q.id === questionId);
    if (!question) return;

    const newOptions = [
      ...(question.options || []),
      {
        id: -Date.now(), // 임시 ID 추가
        schoolid: schoolId, // schoolid 추가
        option_text: `${t("common.survey.optionPlaceholder") || "옵션"} ${
          (question.options?.length || 0) + 1
        }`,
        order: (question.options?.length || 0) + 1,
      },
    ];

    handleTeacherQuestionChange(questionId, "options", newOptions);
  };

  const updateTeacherQuestionOption = (questionId, optionId, value) => {
    const question = displayTeacherQuestions.find((q) => q.id === questionId);
    if (!question || !question.options) return;

    const updatedOptions = question.options.map((opt) =>
      opt.id === optionId ? { ...opt, option_text: value } : opt
    );

    handleTeacherQuestionChange(questionId, "options", updatedOptions);
  };

  const deleteTeacherQuestionOption = (questionId, optionId) => {
    const question = displayTeacherQuestions.find((q) => q.id === questionId);
    if (!question || !question.options) return;

    const updatedOptions = question.options.filter(
      (opt) => opt.id !== optionId
    );
    handleTeacherQuestionChange(questionId, "options", updatedOptions);
  };

  // 🔥 새 교사 질문 추가
  const addTeacherQuestion = () => {
    if (!selectedSurvey || !lessonInfo) {
      console.log("addTeacherQuestion: selectedSurvey 또는 lessonInfo가 없음", {
        selectedSurvey,
        lessonInfo,
      });
      return;
    }

    // 임시 ID 생성 (음수로 해서 서버 ID와 구분)
    const tempId = -Date.now();

    // 마지막 질문의 order 찾기 (공통 질문 order 이후부터)
    const maxCommonOrder =
      commonQuestions.length > 0
        ? Math.max(...commonQuestions.map((q) => q.order || 0))
        : 0;

    const maxTeacherOrder =
      displayTeacherQuestions.length > 0
        ? Math.max(...displayTeacherQuestions.map((q) => q.order || 0))
        : maxCommonOrder;

    const newQuestion = {
      id: tempId,
      schoolid: schoolId,
      survey_id: selectedSurvey.id,
      question_text: t("common.survey.questionPlaceholder"),
      question_type: "short_answer",
      question_description: null,
      input_type: "text",
      is_required: false,
      is_common: false, // 🔥 교사 질문이므로 false
      subject_id: lessonInfo.id, // 🔥 현재 과목 ID
      reflect_in_document: false,
      order: maxTeacherOrder + 1,
      extra_reason: null,
      is_active: true,
      isNew: true, // 새로 추가된 질문임을 표시
      options: [], // 🔥 빈 배열로 확실히 초기화
    };

    // 교사 질문 순서 상태에 직접 추가
    setTeacherQuestionOrder([...displayTeacherQuestions, newQuestion]);

    // 🔥 새 질문 추가 시 변경사항 있음으로 표시
    setHasUnsavedChanges(true);

    handleToastCenterTop(t("common.survey.questionAdded"), 2000, "#28a745");
  };

  // 🔥 교사 질문 복사
  const copyTeacherQuestion = (questionId) => {
    const questionToCopy = displayTeacherQuestions.find(
      (q) => q.id === questionId
    );
    if (!questionToCopy) return;

    // 임시 ID 생성 (음수로 해서 서버 ID와 구분)
    const tempId = -Date.now();

    // 마지막 질문의 order 찾기 (공통 질문 order 이후부터)
    const maxCommonOrder =
      commonQuestions.length > 0
        ? Math.max(...commonQuestions.map((q) => q.order || 0))
        : 0;

    const maxTeacherOrder =
      displayTeacherQuestions.length > 0
        ? Math.max(...displayTeacherQuestions.map((q) => q.order || 0))
        : maxCommonOrder;

    const newQuestion = {
      ...questionToCopy,
      id: tempId, // 임시 ID 설정
      question_text:
        questionToCopy.question_text + " (" + t("common.survey.copy") + ")",
      order: maxTeacherOrder + 1,
      is_common: false, // 교사 질문이므로 false
      subject_id: lessonInfo.id, // 현재 과목 ID
      is_active: true, // 복사된 질문도 기본값은 활성 상태
      isNew: true, // 새로 추가된 질문임을 표시
      // 옵션이 있다면 각 옵션에도 임시 ID 추가
      options:
        questionToCopy.options?.map((option, index) => ({
          ...option,
          id: -(Date.now() + index + 1), // 각 옵션에 고유한 임시 ID
          schoolid: schoolId, // schoolid 추가
        })) || [],
    };

    // 교사 질문 순서 상태에 직접 추가
    setTeacherQuestionOrder([...displayTeacherQuestions, newQuestion]);

    // 🔥 질문 복사 시 변경사항 있음으로 표시
    setHasUnsavedChanges(true);

    handleToastCenterTop(t("common.survey.questionAdded"), 2000, "#28a745");
  };

  // 🔥 교사 질문 삭제
  const deleteTeacherQuestion = (questionId) => {
    const questionToDelete = displayTeacherQuestions.find(
      (q) => q.id === questionId
    );
    if (!questionToDelete) return;

    // 새로 추가된 질문 (음수 ID)인 경우 로컬에서만 제거
    if (questionId < 0) {
      setTeacherQuestionOrder((prevOrder) =>
        prevOrder.filter((q) => q.id !== questionId)
      );
      setEditingTeacherQuestions((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });

      // 🔥 새 질문 삭제 후 변경사항 상태 업데이트
      // 새 질문 삭제는 로컬 변경이므로 간단히 확인
      const hasEditingQuestions =
        Object.keys(editingTeacherQuestions).length > 0;
      const hasOtherNewQuestions = displayTeacherQuestions
        .filter((q) => q.id !== questionId)
        .some((q) => q.id < 0);
      setHasUnsavedChanges(hasEditingQuestions || hasOtherNewQuestions);

      handleToastCenterTop(t("common.survey.questionDeleted"), 2000, "#28a745");
      return;
    }

    // 기존 교사 질문인 경우 확인 후 삭제
    setToastConfirmModalState({
      visible: true,
      message: `"${questionToDelete.question_text}" ${t(
        "common.survey.deleteQuestionWarning"
      )}\n\n${t("common.survey.deleteQuestionConfirm")}`,
      onConfirm: async () => {
        try {
          // 서버에서 질문 삭제
          await deleteSurveyQuestion.mutateAsync(questionId);

          // 로컬 상태에서도 제거
          setTeacherQuestionOrder((prevOrder) =>
            prevOrder.filter((q) => q.id !== questionId)
          );
          setEditingTeacherQuestions((prev) => {
            const newState = { ...prev };
            delete newState[questionId];
            return newState;
          });

          // 🔥 삭제 성공 후 변경사항 상태 즉시 업데이트
          // DB에서 삭제되었으므로 다른 변경사항이 있는지 간단히 확인
          const hasEditingQuestions =
            Object.keys(editingTeacherQuestions).length > 0;
          const hasNewQuestions = displayTeacherQuestions
            .filter((q) => q.id !== questionId)
            .some((q) => q.id < 0);
          setHasUnsavedChanges(hasEditingQuestions || hasNewQuestions);

          handleToastCenterTop(
            t("common.survey.questionDeleted"),
            3000,
            "#28a745"
          );
        } catch (error) {
          console.error("질문 삭제 오류:", error);
          handleToastCenterTop(
            t("common.survey.failDelete") + ": " + error.message,
            3000,
            "#dc3545"
          );
        }
      },
      onCancel: () => {
        // 취소 시 아무것도 하지 않음
      },
    });
  };

  // 서버 데이터가 변경될 때 로컬 상태 초기화
  useEffect(() => {
    if (teacherQuestions.length > 0) {
      // 서버 데이터를 순서대로 정렬해서 teacherQuestionOrder에 설정
      const sortedQuestions = [...teacherQuestions].sort(
        (a, b) => a.order - b.order
      );
      setTeacherQuestionOrder(sortedQuestions);
    } else {
      // 서버에 교사 질문이 없으면 빈 배열
      setTeacherQuestionOrder([]);
    }
  }, [teacherQuestions]);

  // 설문 변경 시 기존 교사 질문이 있는지 확인하고 경고
  const handleSurveyChange = (newSurveyId) => {
    if (
      selectedSurveyId &&
      selectedSurveyId !== newSurveyId &&
      hasUnsavedChanges
    ) {
      setToastConfirmModalState({
        visible: true,
        message: t("common.survey.unsavedChangesWarning"),
        onConfirm: () => {
          setSelectedSurveyId(newSurveyId);
          setHasUnsavedChanges(false);
          // 🔥 상태 초기화
          setEditingTeacherQuestions({});
          setTeacherQuestionOrder([]);
        },
        onCancel: () => {
          // 취소 시 아무것도 하지 않음
        },
      });
    } else {
      setSelectedSurveyId(newSurveyId);
      // 🔥 설문 변경 시 상태 초기화
      setEditingTeacherQuestions({});
      setTeacherQuestionOrder([]);
    }
  };

  // 첫 번째 설문을 기본 선택
  useEffect(() => {
    if (qualifyingSurveys.length > 0 && !selectedSurveyId) {
      setSelectedSurveyId(qualifyingSurveys[0].id.toString());
    }
  }, [qualifyingSurveys, selectedSurveyId]);

  /* 자 생각해 보자. 
useEffect안에 qualifyingSurveys로 isPublished 객체를 만들고
이것이 체크박스에 반영되고
체크박스가 변경되면 낙관적 UX 기번으로 setIspublished 를 먼저 바꾸고, 
서버에 통신 보내어서 저장하고, 
성공하면 qualfyingSurveys를 reset하고
실패하면 setIspublished 를 원래대로 돌리고 에러 메시지 띄우기
*/

  // 🔥 선택된 설문이 변경될 때마다 공개 상태 업데이트
  useEffect(() => {
    if (selectedSurvey && lessonInfo) {
      const subjectLink = selectedSurvey.subjectLinks?.find(
        (link) => link.subject_id === lessonInfo.id
      );
      setIsPublished(subjectLink?.in_use || false);
    }
  }, [selectedSurvey, lessonInfo]);

  const handleIsPublishedChange = (checked) => {
    if (isUpdatingPublishStatus) return; // 연속 클릭 방지

    setIsUpdatingPublishStatus(true);
    setIsPublished(checked);

    // 시작 시간 기록
    const startTime = Date.now();

    createOrUpdateSurveySubjectLink.mutate(
      {
        schoolid: schoolId,
        survey_id: selectedSurvey.id,
        subject_id: lessonInfo.id,
        in_use: checked,
      },
      {
        onSuccess: () => {
          handleToastCenterTop(
            checked ? "설문이 공개되었습니다" : "설문이 비공개되었습니다",
            2000,
            "#28a745"
          );
        },
        onError: (error) => {
          console.error("설문 공개 상태 변경 오류:", error);
          handleToastCenterTop(
            t("common.survey.failUpdate") + ": " + error.message,
            3000,
            "#dc3545"
          );
          setIsPublished(!checked);
        },
        onSettled: () => {
          // 최소 500ms 보장
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, 500 - elapsed);

          setTimeout(() => {
            setIsUpdatingPublishStatus(false);
          }, remainingTime);
        },
      }
    );
  };

  // 🔥 교사 질문 저장 함수
  const saveTeacherQuestions = () => {
    if (!selectedSurvey || !lessonInfo) return;

    // 교사 질문 데이터 준비 (새 질문 + 기존 질문)
    const teacherQuestionsToSave = displayTeacherQuestions.map((question) => {
      // 새로운 질문 (음수 ID)인 경우 id와 임시 필드 제거
      if (question.id < 0) {
        const { id, isNew, ...questionData } = question;

        // 옵션이 있으면 옵션에서도 임시 ID 제거
        if (questionData.options) {
          questionData.options = questionData.options.map((option) => {
            const { id: optionId, ...optionData } = option;
            return optionData;
          });
        }

        return {
          ...questionData,
          subject_id: lessonInfo.id, // 현재 과목 ID 확실히 설정
        };
      } else {
        // 기존 질문인 경우 편집된 내용 병합
        const editedData = editingTeacherQuestions[question.id];
        const updatedQuestion = editedData
          ? { ...question, ...editedData }
          : question;

        // 옵션 처리: 새로운 옵션(음수 ID)은 id 제거, 기존 옵션(양수 ID)은 id 유지
        if (updatedQuestion.options) {
          updatedQuestion.options = updatedQuestion.options.map((option) => {
            if (option.id < 0) {
              // 새로운 옵션: id 제거
              const { id: optionId, ...optionData } = option;
              return optionData;
            } else {
              // 기존 옵션: id 유지
              return option;
            }
          });
        }

        return {
          ...updatedQuestion,
          subject_id: lessonInfo.id, // 현재 과목 ID 확실히 설정
        };
      }
    });

    if (teacherQuestionsToSave.length > 0) {
      // 교사 질문들을 서버로 전송
      createOrUpdateSurveyQuestions.mutate(teacherQuestionsToSave, {
        onSuccess: () => {
          // 🔥 저장 성공 시 즉시 변경사항 상태 초기화
          setEditingTeacherQuestions({});
          setHasUnsavedChanges(false);

          handleToastCenterTop(t("common.survey.surveySaved"), 3000, "#28a745");
        },
        onError: (error) => {
          console.error("교사 질문 저장 오류:", error);
          handleToastCenterTop(
            t("common.survey.failUpdate") + ": " + error.message,
            3000,
            "#dc3545"
          );
        },
      });
    } else {
      // 저장할 질문이 없는 경우에도 상태 초기화
      setEditingTeacherQuestions({});
      setHasUnsavedChanges(false);

      handleToastCenterTop(t("common.survey.surveySaved"), 3000, "#28a745");
    }
  };

  // 🔥 미리보기 함수 - SessionStorage 방식
  const handlePreview = () => {
    if (!selectedSurvey) return;

    // 모든 질문 합치기 (공통 + 교사)
    const allQuestions = [
      ...commonQuestions,
      ...displayTeacherQuestions.filter((q) => q.is_active !== false),
    ].sort((a, b) => a.order - b.order);

    // 미리보기 데이터 준비
    const previewData = {
      survey: {
        title: selectedSurvey.title,
        survey_type: selectedSurvey.survey_type,
        response_target: selectedSurvey.response_target,
        is_anonymous: selectedSurvey.is_anonymous,
      },
      questions: allQuestions.map((question) => ({
        id: question.id,
        question_text: question.question_text,
        question_type: question.question_type,
        question_description: question.question_description,
        is_required: question.is_required,
        options:
          question.options?.map((option) => ({
            id: option.id,
            option_text: option.option_text,
            order: option.order,
          })) || [],
      })),
      theme: selectedSurvey.theme || "classic",
      description: selectedSurvey.description || "",
    };

    // SessionStorage에 데이터 저장
    const previewId = Date.now().toString();
    sessionStorage.setItem(
      `surveyPreview_${previewId}`,
      JSON.stringify(previewData)
    );

    // 짧은 URL로 미리보기 페이지 열기
    const previewUrl = `/${schoolId}/teacher/survey-preview?id=${previewId}`;
    window.open(previewUrl, "_blank");
  };

  // 🔥 모달 닫기 전 변경사항 확인
  const handleClose = () => {
    if (hasUnsavedChanges) {
      setToastConfirmModalState({
        visible: true,
        message: t("common.survey.unsavedChangesWarning"),
        onConfirm: () => {
          onClose();
        },
        onCancel: () => {
          // 취소 시 아무것도 하지 않음
        },
      });
    } else {
      onClose();
    }
  };

  // 🔥 드래그 앤 드롭 핸들러들
  const handleDragStart = (e, question) => {
    setDraggedQuestion(question);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget.outerHTML);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDragEnter = (e, targetQuestion) => {
    e.preventDefault();
    setDragOverTargetId(targetQuestion.id);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverTargetId(null);
  };

  const handleDrop = (e, targetQuestion) => {
    e.preventDefault();
    setDragOverTargetId(null);

    if (!draggedQuestion || draggedQuestion.id === targetQuestion.id) {
      setDraggedQuestion(null);
      return;
    }

    const draggedIndex = displayTeacherQuestions.findIndex(
      (q) => q.id === draggedQuestion.id
    );
    const targetIndex = displayTeacherQuestions.findIndex(
      (q) => q.id === targetQuestion.id
    );

    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedQuestion(null);
      return;
    }

    // 질문 순서 상태 업데이트
    const reorderedQuestions = [...displayTeacherQuestions];
    const [removed] = reorderedQuestions.splice(draggedIndex, 1);
    reorderedQuestions.splice(targetIndex, 0, removed);

    // 공통 질문 다음부터 순서 값 재계산
    const maxCommonOrder =
      commonQuestions.length > 0
        ? Math.max(...commonQuestions.map((q) => q.order || 0))
        : 0;

    // 순서 값 업데이트 (공통 질문 order 이후부터)
    const updatedQuestions = reorderedQuestions.map((question, index) => ({
      ...question,
      order: maxCommonOrder + index + 1,
    }));

    setTeacherQuestionOrder(updatedQuestions);
    setDraggedQuestion(null);

    // 🔥 순서 변경 시 변경사항 있음으로 표시
    setHasUnsavedChanges(true);

    // 사용자에게 피드백
    handleToastCenterTop(
      t("common.survey.questionOrderChanged"),
      2000,
      "#28a745"
    );
  };

  const handleDragEnd = () => {
    setDraggedQuestion(null);
    setDragOverTargetId(null);
  };

  if (!lessonInfo) {
    return null;
  }

  return (
    <div className={styles.modalContainer}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div>
            <h3 className={styles.modalTitle}>
              📋 {t("common.survey.setting") || "설문지 설정"}
            </h3>
            <div className="mt-1 ms-4" style={{ fontSize: "13px" }}>
              {t("teacher.lessonRelated.subject") || "과목"}:{" "}
              {lessonInfo.subject_name} / {t("common.user.teacher") || "교사"}:{" "}
              {Array.isArray(lessonInfo.teachers)
                ? lessonInfo.teachers.map((t) => t.tname).join(", ")
                : ""}{" "}
              / {t("common.labels.studentYear") || "학년"}-
              {t("common.labels.class") || "반"}: {lessonInfo.hierarchyTwo}-
              {lessonInfo.hierarchyThree} /
              {t("common.labels.termName") || "학기"}: {lessonInfo.termName}
            </div>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleClose}
          >
            ✕
          </button>
        </div>

        <div className={styles.modalContent}>
          {/* 설문 선택 드롭다운 */}
          <div className={styles.surveySelect}>
            <div>
              <label
                htmlFor="surveySelect"
                className={styles.surveySelectLabel}
                style={{ paddingTop: "6px" }}
              >
                📊 {t("common.survey.title") || "설문지"}{" "}
                {t("common.labels.select") || "선택"}
              </label>
            </div>
            <div style={{ flex: 1 }}>
              <select
                id="surveySelect"
                className={styles.surveySelectInput}
                value={selectedSurveyId}
                onChange={(e) => handleSurveyChange(e.target.value)}
              >
                <option value="">
                  {t("common.survey.selectSurveyPlaceholder") ||
                    "설문지를 선택하세요"}
                </option>
                {qualifyingSurveys.map((survey) => {
                  const subjectLink = survey.subjectLinks?.find(
                    (link) => link.subject_id === lessonInfo.id
                  );
                  return (
                    <option
                      key={survey.id}
                      value={survey.id}
                      style={{
                        color: subjectLink?.in_use ? "#28a745" : "#6c757d",
                        fontWeight: subjectLink?.in_use ? "500" : "normal",
                        opacity: subjectLink?.in_use ? 1 : 0.7,
                      }}
                    >
                      {subjectLink?.in_use
                        ? `(✅ ${t("common.survey.published") || "설문공개"}) `
                        : `(⬜ ${t("common.survey.unpublished") || "비공개"}) `}
                      {survey.title}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          {/* 선택된 설문 정보 및 질문 표시 영역 */}
          {selectedSurvey && (
            <div className={styles.surveyInfo}>
              <div className="d-flex justify-content-between">
                <h5 className={styles.surveyTitle}>
                  📋 {selectedSurvey.title}
                </h5>
                <div className="d-flex align-items-center mb-2">
                  <span
                    className={styles.surveyDescriptionText}
                    style={{
                      fontSize: "14px",
                      marginRight: "10px",
                      fontWeight: "bold",
                    }}
                  >
                    {t("common.survey.published") || "설문공개"}
                  </span>
                  <input
                    type="checkbox"
                    checked={isPublished}
                    disabled={isUpdatingPublishStatus}
                    onChange={(e) => handleIsPublishedChange(e.target.checked)}
                    style={{
                      width: "18px",
                      height: "18px",
                      accentColor: "#28a745",
                      cursor: isUpdatingPublishStatus
                        ? "not-allowed"
                        : "pointer",
                      opacity: isUpdatingPublishStatus ? 0.6 : 1,
                    }}
                  />
                </div>
              </div>

              {/* 설문 기본 정보 */}
              <div className={styles.surveyMeta}>
                <div className={styles.surveyMetaItem}>
                  <span className={styles.surveyMetaLabel}>
                    {t("common.survey.surveyType") || "설문 유형"}
                  </span>
                  <span className={styles.surveyMetaValue}>
                    {t(`common.survey.${selectedSurvey.survey_type}`)}
                  </span>
                </div>
                <div className={styles.surveyMetaItem}>
                  <span className={styles.surveyMetaLabel}>
                    {t("common.survey.responseTarget") || "응답 대상"}
                  </span>
                  <span className={styles.surveyMetaValue}>
                    {t(`common.survey.${selectedSurvey.response_target}`)}
                  </span>
                </div>
                <div className={styles.surveyMetaItem}>
                  <span className={styles.surveyMetaLabel}>
                    {t("common.survey.isAnonymous") || "익명 여부"}
                  </span>
                  <span className={styles.surveyMetaValue}>
                    {selectedSurvey.is_anonymous
                      ? t("common.survey.anonymous") || "익명"
                      : t("common.survey.no") === "X"
                      ? "실명"
                      : t("common.survey.no") || "실명"}
                  </span>
                </div>
              </div>

              {/* 설문 설명 */}
              {selectedSurvey.description && (
                <div className={styles.surveyDescription}>
                  <span className={styles.surveyDescriptionLabel}>
                    {t("common.survey.description") || "설명"}
                  </span>
                  <p className={styles.surveyDescriptionText}>
                    {selectedSurvey.description}
                  </p>
                </div>
              )}

              {/* 질문 목록 표시 영역 */}
              <div className={styles.questionsSection}>
                {surveyQuestionsAllQuery.isLoading && (
                  <div className={styles.loading}>
                    <div className={styles.loadingSpinner}></div>
                    <div>{t("common.survey.loadingSurvey")}</div>
                  </div>
                )}

                {surveyQuestionsAllQuery.data && (
                  <div>
                    {/* 🔥 공통 질문 섹션 */}
                    <div className={styles.commonQuestions}>
                      <div className={styles.sectionHeader}>
                        <div className="d-flex align-items-center">
                          <span className={styles.sectionBadge}>
                            {t("common.labels.all")}
                          </span>
                          <h6 className={styles.sectionTitle}>
                            {t("common.labels.all")}{" "}
                            {t("common.survey.question")} (
                            {commonQuestions.length}개)
                          </h6>
                        </div>
                        <div
                          className={styles.warningMessage}
                          style={{
                            fontSize: "14px",
                            padding: "4px",
                            marginTop: "0px",
                          }}
                        >
                          ⚠️ {t("common.survey.noModifyCommonQuestion")}
                        </div>
                      </div>
                      {commonQuestions.length > 0 ? (
                        <div className={styles.commonQuestionsContainer}>
                          {commonQuestions.map((question, index) => (
                            <div
                              key={question.id}
                              className={styles.commonQuestionItem}
                            >
                              <span className={styles.questionNumber}>
                                Q{index + 1}.
                              </span>
                              <span className={styles.questionText}>
                                {question.question_text}
                              </span>
                              <span className={styles.questionBadge}>
                                {getQuestionTypeLabel(question.question_type)}
                              </span>
                              {question.is_required && (
                                <span
                                  className={`${styles.questionBadge} ${styles.required}`}
                                >
                                  {t("common.survey.required")}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.noQuestionsMessage}>
                          {t("common.labels.all") || "공통"}{" "}
                          {t("common.survey.question") || "질문"}이 없습니다.
                        </div>
                      )}
                    </div>

                    {/* 🔥 교사 질문 섹션 */}
                    <div className={styles.teacherQuestions}>
                      <div className={styles.teacherQuestionsHeader}>
                        <div className={styles.sectionHeader}>
                          <span
                            className={`${styles.sectionBadge} ${styles.primary}`}
                          >
                            {t("common.user.teacher")}
                          </span>
                          <h6 className={styles.sectionTitle}>
                            {t("common.user.teacher")}{" "}
                            {t("common.survey.question")} (
                            {displayTeacherQuestions.length}개)
                          </h6>
                        </div>
                      </div>

                      {displayTeacherQuestions.length > 0 ? (
                        <div className={styles.teacherQuestionsContainer}>
                          {displayTeacherQuestions.map((question, index) => (
                            <div
                              key={question.id}
                              className={
                                dragOverTargetId === question.id
                                  ? styles.dragOver
                                  : ""
                              }
                              onDragOver={handleDragOver}
                              onDragEnter={(e) => handleDragEnter(e, question)}
                              onDragLeave={handleDragLeave}
                              onDrop={(e) => handleDrop(e, question)}
                            >
                              <TeacherQuestionItem
                                question={question}
                                index={index}
                                commonQuestionsLength={commonQuestions.length}
                                onUpdate={handleTeacherQuestionChange}
                                onCopy={copyTeacherQuestion}
                                onDelete={deleteTeacherQuestion}
                                onAddOption={addTeacherQuestionOption}
                                onUpdateOption={updateTeacherQuestionOption}
                                onDeleteOption={deleteTeacherQuestionOption}
                                onDragStart={handleDragStart}
                                onDragEnd={handleDragEnd}
                                isDragging={draggedQuestion?.id === question.id}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className={styles.noQuestionsMessage}>
                          {t("common.user.teacher") || "교사"}{" "}
                          {t("common.survey.question") || "질문"}이 없습니다. "
                          {t("common.survey.addQuestion") || "질문 추가"}"
                          버튼을 클릭하여 새 질문을 만드세요.
                        </div>
                      )}
                      <div className="text-end">
                        <button
                          type="button"
                          className={styles.addQuestionButton}
                          onClick={addTeacherQuestion}
                          disabled={!selectedSurvey}
                        >
                          + {t("common.survey.addQuestion") || "질문 추가"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {surveyQuestionsAllQuery.error && (
                  <div className={styles.errorMessage}>
                    {t("common.survey.loadingData") ||
                      "설문 데이터를 불러오는데 실패했습니다."}
                  </div>
                )}
              </div>
            </div>
          )}

          {!selectedSurvey && qualifyingSurveys.length === 0 && (
            <div className={styles.warningAlert}>
              <strong>
                ⚠️ 수정 가능한 {t("common.survey.title") || "설문지"}가
                없습니다.
              </strong>
              <br />
              <small>
                조건: survey_type='bySubject', status≠'inactive',
                allow_question_add=true
              </small>
            </div>
          )}
        </div>

        {/* 🔥 변경사항 표시가 포함된 모달 푸터 */}
        <div className={styles.modalFooter}>
          {hasUnsavedChanges ? (
            <div className={styles.unsavedChanges}>
              ⚠️{" "}
              {t("common.survey.unsavedChanges") ||
                "저장되지 않은 변경사항이 있습니다."}
            </div>
          ) : (
            <div></div>
          )}
          <div className={styles.footerButtons}>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.secondary}`}
              onClick={handleClose}
            >
              {t("common.labels.cancel")}
            </button>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.primary}`}
              disabled={
                !selectedSurvey || createOrUpdateSurveyQuestions.isLoading
              }
              onClick={saveTeacherQuestions}
            >
              {createOrUpdateSurveyQuestions.isLoading ? (
                <>
                  <span className={styles.buttonSpinner}></span>
                  {t("common.status.saving")}...
                </>
              ) : (
                "💾 " + t("common.survey.save")
              )}
            </button>
            <button
              type="button"
              className={`${styles.footerButton} ${styles.outline}`}
              disabled={!selectedSurvey}
              onClick={handlePreview}
            >
              👁️ {t("common.survey.preview")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
